import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Building, Clock } from 'lucide-react';

const InternalLinks = ({ currentPage = '', excludeCurrent = true }) => {
  const serviceLinks = [
    {
      title: 'Attorney Services Portal',
      description: 'Dedicated support platform for law firms and legal professionals',
      path: '/attorney-service', 
      icon: Clock,
      keywords: 'attorney services, law firm support, legal professionals'
    },
    {
      title: 'Process Serving Services',
      description: 'Statewide registered servers with same-day options available',
      path: '/process-serving',
      icon: FileText,
      keywords: 'process serving, legal documents, California'
    },
    {
      title: 'Court Filing Services', 
      description: 'E-filing and traditional filing for all California courts',
      path: '/court-filing',
      icon: Building,
      keywords: 'court filing, e-filing, California courts'
    },
    {
      title: 'Document Retrieval',
      description: 'Fast retrieval of court records and legal documents',
      path: '/document-retrieval',
      icon: FileText,
      keywords: 'document retrieval, court records, legal documents'
    },
    {
      title: 'Skip Tracing Services',
      description: 'Professional people location and investigation services',
      path: '/skip-tracing',
      icon: FileText,
      keywords: 'skip tracing, people location, investigation'
    },
    {
      title: 'County Recording',
      description: 'Recording services for all 58 California counties',
      path: '/county-recording',
      icon: Building,
      keywords: 'county recording, document recording, California counties'
    }
  ];

  const contentLinks = [
    {
      title: 'Legal Document Services Blog',
      description: 'Expert tips and guides on process serving and court filing',
      path: '/blog',
      type: 'Blog'
    },
    {
      title: 'California Service Areas',
      description: 'Complete list of cities and counties we serve',
      path: '/service-areas',
      type: 'Service Areas'
    },
    {
      title: 'Place Order Now',
      description: 'Start your order for immediate service',
      path: '/place-order',
      type: 'Order'
    }
  ];

  const cityLinks = [
    { title: 'Palm Springs Process Server', path: '/cities/palm-springs' },
    { title: 'Riverside Process Server', path: '/cities/riverside' }, 
    { title: 'Temecula Process Server', path: '/cities/temecula' },
    { title: 'Indio Process Server', path: '/cities/indio' },
    { title: 'Palm Desert Process Server', path: '/cities/palm-desert' },
    { title: 'La Quinta Process Server', path: '/cities/la-quinta' }
  ];

  // Filter out current page if specified
  const filterLinks = (links) => {
    if (!excludeCurrent) return links;
    return links.filter(link => link.path !== currentPage);
  };

  return (
    <section style={{
      backgroundColor: 'var(--bg-card)',
      padding: '60px 0',
      borderTop: '1px solid var(--border-light)'
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Related Legal Services
          </h2>
          <p className="body-medium" style={{ 
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Explore our comprehensive legal document services throughout California
          </p>
        </div>

        {/* Service Links */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 className="heading-3" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Our Services
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {filterLinks(serviceLinks).map((link, index) => {
              const IconComponent = link.icon;
              return (
                <Link
                  key={index}
                  to={link.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.5rem',
                    backgroundColor: 'var(--bg-page)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-medium)',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  className="internal-link-card"
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={20} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 className="heading-4" style={{
                      fontSize: '1.1rem',
                      marginBottom: '0.5rem',
                      color: 'var(--text-primary)'
                    }}>
                      {link.title}
                    </h4>
                    <p className="body-small" style={{
                      color: 'var(--text-secondary)',
                      margin: 0
                    }}>
                      {link.description}
                    </p>
                  </div>
                  <ArrowRight size={16} color="var(--brand-primary)" />
                </Link>
              );
            })}
          </div>
        </div>

        {/* Local Service Areas */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 className="heading-3" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Local Service Areas
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {cityLinks.map((city, index) => (
              <Link
                key={index}
                to={city.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--bg-page)',
                  border: '1px solid var(--border-medium)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                className="city-link"
              >
                <span style={{
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: 'var(--text-primary)'
                }}>
                  {city.title}
                </span>
                <ArrowRight size={14} color="var(--brand-primary)" />
              </Link>
            ))}
          </div>
        </div>

        {/* Content Links */}
        <div>
          <h3 className="heading-3" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Resources & Information
          </h3>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {filterLinks(contentLinks).map((link, index) => (
              <Link
                key={index}
                to={link.path}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 1.5rem',
                  backgroundColor: 'var(--bg-page)',
                  border: '2px solid var(--border-medium)',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
                className="content-link"
              >
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: 'var(--text-primary)'
                }}>
                  {link.title}
                </span>
                <ArrowRight size={16} color="var(--brand-primary)" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .internal-link-card:hover {
          transform: translateY(-2px);
          border-color: var(--brand-primary);
          box-shadow: 0 4px 12px rgba(44, 90, 160, 0.1);
        }

        .content-link:hover {
          background-color: var(--brand-primary);
          color: white;
          border-color: var(--brand-primary);
        }

        .content-link:hover span {
          color: white !important;
        }

        .city-link:hover {
          background-color: var(--bg-card);
          border-color: var(--brand-primary);
          transform: translateX(2px);
        }
      `}</style>
    </section>
  );
};

export default InternalLinks;